import Config from 'react-native-config';
import { inferEnvironmentFromBackendUrl, isAllowedTestPrefixEmail, resolveEnvironmentConfig, resolveSignupUrl, } from '../config/environment';
/**
 * Strips local/test/prod email prefixes and returns the login email plus target environment.
 * Examples: local$user@x.com → user@x.com (local); localhost$user@x.com → same (alias for local).
 *
 * When there is no prefix, uses `environmentWhenNoPrefix` (typically current Redux `environment`)
 * so test.abbisway.com stays on test; falls back to inferring from BACKEND_BASE_URL when omitted.
 * Store builds still honor `test$` → test.abbisway.com (including after production is deployed).
 * `test$` reaches TEST only for allowed accounts (the rest contains "koutavasj");
 * any other `test$` email has the prefix stripped and goes to production.
 * `local$` / `localhost$` target this Mac only in non-store runs. Store builds strip them
 * and stay on production. Deployed hosts are test.abbisway.com and app.abbisway.com.
 */
export function resolveLoginEmailFromInput(rawEmail, environmentWhenNoPrefix, storeBuild = __STORE_BUILD__) {
    const trimmed = rawEmail.trim();
    if (/^test\$/i.test(trimmed)) {
        return {
            email: trimmed.slice(5),
            environment: isAllowedTestPrefixEmail(trimmed) ? 'test' : 'prod',
        };
    }
    if (storeBuild) {
        return {
            email: trimmed.replace(/^(?:localhost|local|prod)\$/i, ''),
            environment: 'prod',
        };
    }
    const lowered = trimmed.toLowerCase();
    // Longer prefixes first so "localhost$" is not confused with "local$".
    const prefixes = [
        { prefix: 'localhost$', environment: 'local' },
        { prefix: 'local$', environment: 'local' },
        { prefix: 'prod$', environment: 'prod' },
    ];
    for (const { prefix, environment } of prefixes) {
        if (lowered.startsWith(prefix)) {
            return {
                email: trimmed.slice(prefix.length),
                environment,
            };
        }
    }
    return {
        email: trimmed,
        environment: environmentWhenNoPrefix ??
            inferEnvironmentFromBackendUrl(Config.BACKEND_BASE_URL),
    };
}
export function applyEnvironmentForLogin(environment) {
    return {
        environment,
        ...resolveEnvironmentConfig(environment),
    };
}
/**
 * D1 signup link: use the same email-prefix env as Sign in.
 * Store builds keep the signup URL on production (`resolveSignupUrl`); login still follows `test$`.
 * Non-store builds support `test$` → test, `local$` → local, and empty → current Redux env.
 * Does not use persisted Redux frontendBaseUrl, which can stay local while
 * the device talks to a LAN backend.
 */
export function resolveSignupTargetFromEmailInput(emailTyped, currentEnvironment) {
    const { environment } = resolveLoginEmailFromInput(emailTyped, currentEnvironment);
    const env = applyEnvironmentForLogin(environment);
    return {
        environment: env.environment,
        frontendBaseUrl: env.frontendBaseUrl,
        signupUrl: resolveSignupUrl(env.frontendBaseUrl),
    };
}
const LOCAL_WEB_HOSTS = new Set(['localhost', '127.0.0.1']);
/** True when in-app Signup is the same host as the resolved env website. */
export function isSameWebSignupHost(frontendBaseUrl, currentHostname) {
    let resolvedHost;
    try {
        resolvedHost = new URL(frontendBaseUrl).hostname.toLowerCase();
    }
    catch {
        return false;
    }
    const current = currentHostname.trim().toLowerCase();
    if (!current) {
        return false;
    }
    if (LOCAL_WEB_HOSTS.has(resolvedHost) && LOCAL_WEB_HOSTS.has(current)) {
        return true;
    }
    return resolvedHost === current;
}
